import { useEffect, useState } from 'react';
import {
  CARRIER_CATEGORIES,
  EXPENSE_CATEGORY_LABEL,
  SHIPPER_CATEGORIES,
  type ExpenseCategory,
  type ExpenseRow,
  type FileRow,
  type OrderEconomics,
} from 'shared';
import { api } from './api';
import { alert, confirm } from './telegram';
import { UploadButton } from './Files';
import NumberField from './NumberField';
import { useFeature } from './features';

const money = (v: number) => `${v.toLocaleString('ru-RU')} ₽`;

/**
 * Расходы по рейсу.
 *
 * Показывается только исполнителю и его диспетчеру — заказчик этот блок
 * не получает вовсе, ни в каком виде.
 */
export default function Expenses({
  orderId,
  side = 'CARRIER',
}: {
  orderId: string;
  /** Чья книга: у заказчика свои статьи. */
  side?: 'CARRIER' | 'SHIPPER';
}) {
  const [items, setItems] = useState<ExpenseRow[]>([]);
  const [eco, setEco] = useState<OrderEconomics | null>(null);
  const [open, setOpen] = useState(false);

  const categories = side === 'SHIPPER' ? SHIPPER_CATEGORIES : CARRIER_CATEGORIES;
  const [category, setCategory] = useState<ExpenseCategory>(categories[0]!);
  const [amount, setAmount] = useState(0);
  const [note, setNote] = useState('');
  const [receipt, setReceipt] = useState<FileRow | null>(null);
  const [busy, setBusy] = useState(false);
  const on = useFeature('expenses');

  async function load() {
    try {
      const r = await api.expenses(orderId);
      setItems(r.items);
      setEco(r.economics);
    } catch {
      // Нет доступа — блок просто не показывается.
    }
  }

  useEffect(() => {
    void load();
  }, [orderId]);

  async function add() {
    if (amount <= 0) {
      await alert('Укажите сумму');
      return;
    }
    setBusy(true);
    try {
      await api.addExpense(orderId, {
        category,
        amountRub: Math.round(amount),
        note: note.trim() || undefined,
        fileId: receipt?.id,
      });
      setAmount(0);
      setNote('');
      setReceipt(null);
      setOpen(false);
      await load();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!(await confirm('Удалить запись?'))) return;
    await api.deleteExpense(id).catch(() => {});
    await load();
  }

  if (!on || !eco) return null;

  return (
    <>
      <h3 style={{ marginTop: 16 }}>
        {side === 'SHIPPER' ? 'Затраты на перевозку' : 'Деньги по рейсу'}
      </h3>

      <div className="eco">
        <div>
          <span className="meta">{side === 'SHIPPER' ? 'Оплата рейса' : 'На руки'}</span>
          <strong>{money(Math.abs(eco.payoutRub))}</strong>
        </div>
        <div>
          <span className="meta">Расходы</span>
          <strong>{money(eco.expensesRub)}</strong>
        </div>
        <div>
          <span className="meta">{side === 'SHIPPER' ? 'Всего' : 'Чистыми'}</span>
          <strong className={side === 'CARRIER' && eco.netRub < 0 ? 'neg' : 'pos'}>
            {money(Math.abs(eco.netRub))}
          </strong>
        </div>
      </div>

      {items.map((e) => (
        <div key={e.id} className="doc-row">
          <span className="meta">
            {EXPENSE_CATEGORY_LABEL[e.category]} · {money(e.amountRub)}
            {e.note ? ` · ${e.note}` : ''}
            {!e.byMe ? ` · ${e.authorName}` : ''}
          </span>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {e.receiptUrl && (
              <a href={e.receiptUrl} target="_blank" rel="noreferrer" className="meta">
                чек
              </a>
            )}
            <button type="button" className="file-del" onClick={() => void remove(e.id)}>
              ×
            </button>
          </span>
        </div>
      ))}

      {!open ? (
        <button className="secondary" onClick={() => setOpen(true)}>
          Добавить расход
        </button>
      ) : (
        <div className="card card--inset" style={{ marginTop: 8 }}>
          <div className="field-row">
            <div className="field">
              <label>Куда</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
              >
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {EXPENSE_CATEGORY_LABEL[c]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Сумма, ₽</label>
              <NumberField value={amount} onChange={setAmount} />
            </div>
          </div>

          <div className="field">
            <label>Заметка</label>
            <input
              value={note}
              placeholder="АЗС на М-4, 320 км"
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <div className="doc-row">
            <span className="meta">{receipt ? 'Чек приложен' : 'Чек не обязателен'}</span>
            <UploadButton
              kind="EXPENSE_RECEIPT"
              label={receipt ? 'Заменить' : 'Чек'}
              onDone={setReceipt}
            />
          </div>

          <div className="row" style={{ gap: 8, marginTop: 8 }}>
            <button disabled={busy} onClick={() => void add()}>
              Сохранить
            </button>
            <button className="secondary" onClick={() => setOpen(false)}>
              Отмена
            </button>
          </div>
        </div>
      )}
    </>
  );
}
